import { computed, ref } from '@vue/composition-api'
import Vue from 'vue'
import store from '../../admin-portal/src/state/store.js'
import { getAdminPortalNavbarItems } from '../utils/api/admin-portal-navigation.js'
import { SEE_COOKIE_MODULE_AREA } from '../permissions/admin-portal-permissions.js'
import { dashboardModuleEnabled, privacyNoticeModuleEnabled, waitForLoad } from './config-keys.js'

const navBarItemsState = ref(null)
const externalLinksState = ref([])
const labels = ref([])
let isLoading = false
let isLoadingPromise = null

const COOKIES_NAV_ID = 48
const ADMINISTRATION_NAV_ID = 2
const PRIVACY_POLICIES_NAV_ID = 9
const EXPORTS_NAV_ID = 44
const FILE_LOADER_NAV_ID = 86
const SYNTHETIC_DASHBOARD_KEY = 'synthetic-dashboard-new'
const SYNTHETIC_BULK_DATA_KEY = 'synthetic-bulk-data'
export const DASHBOARD_NAV_CUSTOM_ACTION = 'dashboard-portal'
export const PRIVACY_NOTICE_NAV_CUSTOM_ACTION = 'privacy-notice-portal'

const cloneNavigation = navigation => navigation.map(topLevel => ({
	...topLevel,
	sublinks: Array.isArray(topLevel.sublinks)
		? topLevel.sublinks.map(subLink => ({
			...subLink,
			deeperSublinks: Array.isArray(subLink.deeperSublinks) ? [...subLink.deeperSublinks] : []
		}))
		: []
}))

// Support/training link text values to move to user menu (case-insensitive)
// These are partial matches - if the text contains any of these, it's considered a support/training link
const SUPPORT_TRAINING_KEYWORDS = [
	'support',
	'training',
	'help',
	'learning hub'
]

// Check if a nav item is a support/training link that should be moved to user menu
const isSupportTrainingLink = item => {
	const text = (item.text || '').toLowerCase().trim()
	// Move Support and Training related links to user menu
	return SUPPORT_TRAINING_KEYWORDS.some(keyword => text.includes(keyword))
}

// Extract support/training links and remove them from navigation
const extractExternalLinks = navigation => {
	const externalLinks = []

	// Filter out support/training items at each level
	const filteredNavigation = navigation
		.filter(topLevel => {
			if (isSupportTrainingLink(topLevel)) {
				externalLinks.push({ ...topLevel, level: 'top' })
				return false
			}
			return true
		})
		.map(topLevel => ({
			...topLevel,
			sublinks: Array.isArray(topLevel.sublinks)
				? topLevel.sublinks
					.filter(subLink => {
						if (isSupportTrainingLink(subLink)) {
							externalLinks.push({ ...subLink, level: 'sub' })
							return false
						}
						return true
					})
					.map(subLink => ({
						...subLink,
						deeperSublinks: Array.isArray(subLink.deeperSublinks)
							? subLink.deeperSublinks.filter(deeperLink => {
								if (isSupportTrainingLink(deeperLink)) {
									externalLinks.push({ ...deeperLink, level: 'deeper' })
									return false
								}
								return true
							})
							: []
					}))
				: []
		}))

	return { filteredNavigation, externalLinks }
}

const buildLabels = navigation => ([
	...navigation
		.filter(({ displayText }) => displayText != null)
		.map(({ displayText, text }) => ({ displayText, text })),
	...navigation
		.map(({ sublinks }) => (sublinks || [])
			.filter(({ displayText }) => displayText != null)
			.map(({ displayText, text }) => ({ displayText, text }))).flat(),
	...navigation
		.map(({ sublinks }) => (sublinks || [])
			.map(({ deeperSublinks }) => (deeperSublinks || [])
				.filter(({ displayText }) => displayText != null)
				.map(({ displayText, text }) => ({ displayText, text }))).flat()).flat()
])

const userHasCookieModulePermission = () => {
	const permissionGetter = store.getters?.['auth/productAreaPermission']
	if (typeof permissionGetter !== 'function') return false
	return permissionGetter(SEE_COOKIE_MODULE_AREA)
}

const isUserLoggedIn = () => Boolean(store.getters?.['auth/loggedIn'])

const removeSyntheticDashboardChild = parent => {
	if (!Array.isArray(parent.sublinks)) {
		parent.sublinks = []
		return
	}
	parent.sublinks = parent.sublinks.filter(child => child.syntheticKey !== SYNTHETIC_DASHBOARD_KEY)
}

const maybeInsertDashboardNavItem = navigation => {
	const cookiesParent = navigation.find(item => item.adminPortalNavigationId === COOKIES_NAV_ID || item.text === 'Cookies')
	if (!cookiesParent) return
	removeSyntheticDashboardChild(cookiesParent)
	if (!dashboardModuleEnabled.value || !isUserLoggedIn() || !userHasCookieModulePermission()) {
		return
	}
	const alreadyExists = cookiesParent.sublinks?.some(child => child.syntheticKey === SYNTHETIC_DASHBOARD_KEY || child.customActionType === DASHBOARD_NAV_CUSTOM_ACTION)
	if (alreadyExists) {
		return
	}
	const newDashboardNavItem = {
		adminPortalNavigationId: SYNTHETIC_DASHBOARD_KEY,
		text: 'Dashboard',
		displayText: 'Dashboard',
		icon: 'mdi-chart-bar',
		isExternal: false,
		route: '',
		deeperSublinks: [],
		customActionType: DASHBOARD_NAV_CUSTOM_ACTION,
		syntheticKey: SYNTHETIC_DASHBOARD_KEY,
		chipLabel: 'NEW'
	}
	cookiesParent.sublinks = Array.isArray(cookiesParent.sublinks)
		? [newDashboardNavItem, ...cookiesParent.sublinks]
		: [newDashboardNavItem]
}

const maybeRedirectPrivacyPoliciesNavItem = navigation => {
	if (!privacyNoticeModuleEnabled.value || !isUserLoggedIn()) {
		return
	}
	const administrationParent = navigation.find(item => item.adminPortalNavigationId === ADMINISTRATION_NAV_ID || item.text === 'Administration')
	if (!administrationParent || !Array.isArray(administrationParent.sublinks)) return
	const privacyPoliciesItem = administrationParent.sublinks.find(
		child => child.adminPortalNavigationId === PRIVACY_POLICIES_NAV_ID || child.text === 'Privacy Policies'
	)
	if (!privacyPoliciesItem) return
	privacyPoliciesItem.customActionType = PRIVACY_NOTICE_NAV_CUSTOM_ACTION
	privacyPoliciesItem.chipLabel = 'NEW'
}

/**
 * Check if a navigation item is the Exports nav item
 * @param {object} item - Navigation item to check
 * @returns {boolean}
 */
const isExportsNavItem = item =>
	item.adminPortalNavigationId === EXPORTS_NAV_ID ||
	(item.route && typeof item.route === 'object' && item.route.name === 'data-exports') ||
	item.route === 'data-exports'

/**
 * Check if a navigation item is the File Loader nav item
 * @param {object} item - Navigation item to check
 * @returns {boolean}
 */
const isFileLoaderNavItem = item =>
	item.adminPortalNavigationId === FILE_LOADER_NAV_ID ||
	(item.route && typeof item.route === 'object' && item.route.name === 'file-loader') ||
	item.route === 'file-loader'

/**
 * Find and remove Exports item from navigation, returning it and its position if found
 * @param {Array} navigation - Top-level navigation items
 * @returns {{ item: object, index: number } | null}
 */
const findAndRemoveExportsItem = navigation => {
	// Check top level
	const topLevelIndex = navigation.findIndex(isExportsNavItem)
	if (topLevelIndex !== -1) {
		const item = navigation.splice(topLevelIndex, 1)[0]
		return { item, index: topLevelIndex }
	}

	// Check sublinks of each top-level item
	for (let i = 0; i < navigation.length; i++) {
		const topLevel = navigation[i]
		if (!Array.isArray(topLevel.sublinks)) continue
		const sublinkIndex = topLevel.sublinks.findIndex(isExportsNavItem)
		if (sublinkIndex !== -1) {
			const item = topLevel.sublinks.splice(sublinkIndex, 1)[0]
			// Insert Bulk Data at top level, after the parent item's position
			return { item, index: i + 1 }
		}

		// Check deeperSublinks using indexed loop
		for (let j = 0; j < topLevel.sublinks.length; j++) {
			const sublink = topLevel.sublinks[j]
			if (!Array.isArray(sublink.deeperSublinks)) continue
			const deeperIndex = sublink.deeperSublinks.findIndex(isExportsNavItem)
			if (deeperIndex !== -1) {
				const item = sublink.deeperSublinks.splice(deeperIndex, 1)[0]
				// Insert Bulk Data at top level, after the parent item's position
				return { item, index: i + 1 }
			}
		}
	}

	return null
}

/**
 * Transform Exports and File Loader into a Bulk Data parent with both as sublinks
 * File Loader is an independent top-level nav item (not nested under Exports)
 * Mutates the navigation array in place
 * @param {Array} navigation - Top-level navigation items
 */
const maybeTransformExportsToBulkData = navigation => {
	if (!Array.isArray(navigation)) return

	// Check if Bulk Data already exists (avoid duplicates)
	const bulkDataExists = navigation.some(item => item.syntheticKey === SYNTHETIC_BULK_DATA_KEY)
	if (bulkDataExists) return

	// Find and remove Exports item
	const exportsResult = findAndRemoveExportsItem(navigation)

	// Find and remove File Loader from top level (independent nav item)
	const fileLoaderIndex = navigation.findIndex(isFileLoaderNavItem)
	let fileLoaderItem = fileLoaderIndex !== -1
		? navigation.splice(fileLoaderIndex, 1)[0]
		: null

	// Backwards compatibility: also check Exports' sublinks for File Loader
	if (!fileLoaderItem && exportsResult) {
		const exportsSublinks = Array.isArray(exportsResult.item.sublinks) ? exportsResult.item.sublinks : []
		const nestedIndex = exportsSublinks.findIndex(sub =>
			sub.adminPortalNavigationId === FILE_LOADER_NAV_ID ||
			sub.route?.name === 'file-loader'
		)
		if (nestedIndex !== -1) {
			fileLoaderItem = exportsSublinks.splice(nestedIndex, 1)[0]
		}
	}

	// Need at least one item to create Bulk Data
	if (!exportsResult && !fileLoaderItem) return

	const bulkDataSublinks = []
	const insertIndex = exportsResult ? exportsResult.index : fileLoaderIndex

	if (exportsResult) {
		bulkDataSublinks.push({
			...exportsResult.item,
			deeperSublinks: Array.isArray(exportsResult.item.deeperSublinks) ? exportsResult.item.deeperSublinks : []
		})
	}

	if (fileLoaderItem) {
		bulkDataSublinks.push({
			...fileLoaderItem,
			deeperSublinks: Array.isArray(fileLoaderItem.deeperSublinks) ? fileLoaderItem.deeperSublinks : []
		})
	}

	// Create Bulk Data top-level item
	const bulkDataItem = {
		adminPortalNavigationId: SYNTHETIC_BULK_DATA_KEY,
		text: 'Bulk Data',
		displayText: 'Bulk Data',
		icon: 'mdi-swap-horizontal',
		isExternal: false,
		route: '',
		syntheticKey: SYNTHETIC_BULK_DATA_KEY,
		sublinks: bulkDataSublinks
	}

	navigation.splice(insertIndex, 0, bulkDataItem)
}

const load = async () => {
	if (isLoading) return
	isLoading = true
	isLoadingPromise = (async () => {
		try {
			const { adminPortalNavigation } = await getAdminPortalNavbarItems()
			await waitForLoad()
			const clonedNavigation = cloneNavigation(adminPortalNavigation)

			// Extract external links (support/training) before other processing
			const { filteredNavigation, externalLinks } = extractExternalLinks(clonedNavigation)
			externalLinksState.value = externalLinks

			// Continue with normal nav processing on filtered navigation
			maybeInsertDashboardNavItem(filteredNavigation)
			maybeRedirectPrivacyPoliciesNavItem(filteredNavigation)
			maybeTransformExportsToBulkData(filteredNavigation)
			navBarItemsState.value = filteredNavigation
			labels.value = buildLabels(filteredNavigation)
			return filteredNavigation
		} finally {
			isLoading = false
		}
	})()
	return isLoadingPromise
}

export const clearNavbarItems = () => {
	navBarItemsState.value = null
}

export const useLabels = value => {
	if (value == null) return ''
	// convert to string
	let result = '' + value
	labels.value.forEach(({ text, displayText }) => {
		// if text is plural remove s
		if (text.endsWith('s')) {
			text = text.slice(0, -1)
		}
		if (value.includes(text)) {
			result = result.replace(text, displayText, 'g')
		}
		// match and replace lowercase
		if (value.includes(text.toLowerCase())) {
			result = result.replace(text.toLowerCase(), displayText.toLowerCase(), 'g')
		}

		if (result.endsWith('ys') && !result.toLowerCase().includes('keys')) {
			result = result.replace('ys', 'ies', 'g')
		}
	})
	return result
}
Vue.filter('useLabels', useLabels)

export const waitForNavBarItemsLoad = async reload => {
	if (isLoadingPromise === null || navBarItemsState.value == null || reload) {
		load()
	}
	return isLoadingPromise
}

export const navBarItems = computed(() => {
	if (navBarItemsState.value === null) {
		load()
		return {}
	}
	return navBarItemsState.value
})

export const externalLinks = computed(() => externalLinksState.value || [])

export const navBarItemsTest = computed(() => navBarItems.value || {})
